import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import { fDate } from '../util/format-time';

import Iconify from '../../theme/iconify/iconify';
import SvgColor from '../../theme/svg-color/svg-color';

function CourseCard({ access, course, enrollInCourse }) {
    const { i18n, t } = useTranslation();
    const navigate = useNavigate();

    const renderAvatar = (
        <Avatar
            id='mISLec_avatar_icon'
            alt='Corpus icon'
            src='/assets/icons/knowledge.png'
            sx={{
                zIndex: 9,
                width: 32,
                height: 32,
                position: 'absolute',
                left: 27,
                bottom: -18,
                scale: '75% !important'
            }}
        />
    );

    const renderTitle = (
        <Link
            color='inherit'
            variant='subtitle2'
            underline='none'
            onClick={() => {
                if (access) navigate(`/course/${course._id}`)
            }}
            sx={{
                height: 44,
                overflow: 'hidden',
                WebkitLineClamp: 2,
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                cursor: 'pointer'
            }}
        >
            {course.title}
        </Link>
    );

    const renderInfo = (
        <Stack
            direction='row'
            flexWrap='wrap'
            spacing={1.5}
            justifyContent='space-between'
            alignItems='center'
            sx={{
                mt: 2,
            }}
        >
            <Stack direction='row'>
                {enrollInCourse &&
                    <Button
                        variant=''
                        startIcon={<Iconify icon='mdi:university' width={16} />}
                        onClick={() => enrollInCourse(course._id)}
                        sx={{
                            borderRadius: '8px !important',
                            backgroundColor: 'rgba(33, 43, 54, 0.08)',
                            fontSize: '0.75rem',
                            '&:hover': {
                                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                                color: 'primary.main'
                            },
                        }}
                    >
                        {t('courses.card.join')}
                    </Button>
                }
            </Stack>
            <Tooltip title={t('courses.card.sections')} arrow>
                <Stack
                    direction='row'
                    sx={{
                        color: 'text.disabled',
                        p: 0.5,
                        borderRadius: '8px',
                        '&:hover': {
                            backgroundColor: 'rgba(145, 158, 171, 0.08)',
                        },
                    }}
                >
                    <Typography variant='caption' sx={{ mr: 0.5 }}>{course.sections.length}</Typography>
                    <Iconify width={16} icon='ion:document-outline' />
                </Stack>
            </Tooltip>
        </Stack>
    );

    const renderDate = (
        <Typography
            variant='caption'
            component='div'
            sx={{
                mb: 2,
                color: 'text.disabled'
            }}
        >
            {fDate(course.timestamp, i18n.language)}
        </Typography>
    );

    const renderShape = (
        <SvgColor
            color='paper'
            src='/assets/icons/shape-avatar.svg'
            sx={{
                width: 80,
                height: 36,
                zIndex: 9,
                left: 3,
                bottom: -16,
                position: 'absolute',
                color: 'background.paper',
                borderRadius: '16px'
            }}
        />
    );

    return (
        <Grid item xs={12} sm={6} md={3} sx={{ mb: 1 }}>
            <Paper elevation={2}>
                <Box sx={{ position: 'relative' }}>
                    {renderShape}
                    {renderAvatar}
                </Box>

                <Box
                    sx={{
                        p: (theme) => theme.spacing(4, 3, 2, 3),
                        backgroundColor: 'white',
                        borderRadius: '16px'
                    }}
                >
                    {renderDate}
                    {renderTitle}
                    {renderInfo}
                </Box>
            </Paper>
        </Grid>
    );
}

CourseCard.propTypes = {
    course: PropTypes.object.isRequired
};

export default CourseCard;